import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import {
  Phone, Calendar, Users, Clock, TrendingUp, TrendingDown, BarChart3,
  CheckCircle2, XCircle, DollarSign, Timer, Zap, Bot, MessageSquareText
} from 'lucide-react';

const METRICS = [
  { label: 'Call Volume', value: '142', change: '+12%', up: true, icon: Phone, color: 'text-blue-600 bg-blue-50' },
  { label: 'Schedule Utilization', value: '87%', change: '+3%', up: true, icon: Calendar, color: 'text-emerald-600 bg-emerald-50' },
  { label: 'No-Show Rate', value: '4.2%', change: '-1.8%', up: false, icon: XCircle, color: 'text-red-600 bg-red-50' },
  { label: 'Avg Wait Time', value: '8 min', change: '-2 min', up: false, icon: Timer, color: 'text-amber-600 bg-amber-50' },
  { label: 'New Patients', value: '18', change: '+5', up: true, icon: Users, color: 'text-violet-600 bg-violet-50' },
  { label: 'Revenue (Copays)', value: '$4,680', change: '+8%', up: true, icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
  { label: 'AI Tasks Completed', value: '554', change: '+22%', up: true, icon: Bot, color: 'text-slate-600 bg-slate-100' },
  { label: 'Staff Time Saved', value: '18.5 hrs', change: '+4 hrs', up: true, icon: Clock, color: 'text-blue-600 bg-blue-50' },
];

const HOURLY = [
  { hour: '8 AM', patients: 2, capacity: 4 }, { hour: '9 AM', patients: 4, capacity: 4 },
  { hour: '10 AM', patients: 3, capacity: 4 }, { hour: '11 AM', patients: 2, capacity: 4 },
  { hour: '12 PM', patients: 1, capacity: 2 }, { hour: '1 PM', patients: 3, capacity: 4 },
  { hour: '2 PM', patients: 4, capacity: 4 }, { hour: '3 PM', patients: 2, capacity: 4 },
  { hour: '4 PM', patients: 1, capacity: 3 },
];

const AGENT_PERF = [
  { name: 'Front Desk Agent', tasks: 142, resolved: 128, rate: '90%', time: '1.2 min' },
  { name: 'Follow-Up Agent', tasks: 256, resolved: 248, rate: '97%', time: '0.5 min' },
  { name: 'Intake Agent', tasks: 89, resolved: 82, rate: '92%', time: '2.5 min' },
  { name: 'Revenue Agent', tasks: 67, resolved: 61, rate: '91%', time: '1.8 min' },
];

const CONVERSION = [
  { label: 'Calls → Appointments', value: '68%', icon: Phone },
  { label: 'New Patient → Registered', value: '92%', icon: Users },
  { label: 'Reminder → Confirmed', value: '85%', icon: MessageSquareText },
  { label: 'No-show → Rescheduled', value: '72%', icon: Calendar },
];

const EchoAnalytics = () => {
  return (
    <div className="space-y-6" data-testid="echo-analytics-page">
      <div>
        <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Analytics & Performance</h1>
        <p className="text-slate-500 mt-1">This month's front office performance metrics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {METRICS.slice(0, 4).map((m, i) => (
          <Card key={i} className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", m.color.split(' ')[1])}><m.icon className={cn("h-4 w-4", m.color.split(' ')[0])} /></div>
              <Badge variant="outline" className={cn("text-[10px] gap-1", m.label.includes('No-Show') || m.label.includes('Wait') ? (m.up ? 'text-red-600' : 'text-emerald-600') : (m.up ? 'text-emerald-600' : 'text-red-600'))}>
                {m.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{m.change}
              </Badge>
            </div>
            <p className="text-2xl font-bold">{m.value}</p>
            <p className="text-xs text-muted-foreground">{m.label}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Schedule Heatmap */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Schedule Utilization</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {HOURLY.map((h, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-12">{h.hour}</span>
                  <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                    <div className={cn("h-full rounded-full transition-all", h.patients / h.capacity > 0.9 ? 'bg-red-400' : h.patients / h.capacity > 0.6 ? 'bg-amber-400' : 'bg-emerald-400')} style={{ width: `${(h.patients / h.capacity) * 100}%` }} />
                  </div>
                  <span className="text-xs font-medium w-10 text-right">{h.patients}/{h.capacity}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Conversion Rates */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Conversion Rates</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {CONVERSION.map((c, i) => (
              <div key={i} className="flex items-center gap-4 p-3 rounded-lg border border-slate-200">
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center"><c.icon className="h-4 w-4 text-slate-500" /></div>
                <div className="flex-1"><p className="text-sm font-medium">{c.label}</p></div>
                <p className="text-lg font-bold">{c.value}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Extended Metrics */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Extended Metrics</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {METRICS.slice(4).map((m, i) => (
                <div key={i} className="p-3 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={cn("h-6 w-6 rounded flex items-center justify-center", m.color.split(' ')[1])}><m.icon className={cn("h-3 w-3", m.color.split(' ')[0])} /></div>
                    <span className="text-xs text-muted-foreground">{m.label}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <p className="text-lg font-bold">{m.value}</p>
                    <Badge variant="outline" className={cn("text-[9px]", m.up ? 'text-emerald-600' : 'text-red-600')}>{m.change}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Agent Performance */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">AI Agent Performance</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {AGENT_PERF.map((a, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-lg border border-slate-200" data-testid={`agent-perf-${i}`}>
                  <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center"><Bot className="h-4 w-4 text-slate-500" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{a.name}</p><p className="text-[10px] text-muted-foreground">{a.tasks} tasks · {a.time} avg</p></div>
                  <div className="text-right"><p className="text-sm font-bold">{a.rate}</p><p className="text-[10px] text-muted-foreground">resolve rate</p></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EchoAnalytics;
